import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { useEventListener } from 'ahooks';
import { Select, NumberInput, Loader, Tooltip } from '@mantine/core';
import { translate } from '@shakkerai/util-i18n';
import { mc, mv } from '@shakkerai/report';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { NumberInputGroup, notifySuccess, GuidePopover } from '@shakkerai/pro-components';
import { Icon } from '@shakkerai/components';
import { useLoadings } from '@shakkerai/pro-store';
import { MAX_GENERATOR_SIZE } from '@shakkerai/pro-constants';
import { BASETYPE_VALUE_TO_LABEL, BASETYPE_VALUE_TO_NAME, MODEL_FLUX } from '@shakkerai/pro-models';
import { controlNetImgRefList, simpleModeSupportedControlNets } from '@shakkerai/pro-constants/src/controlnet';
import { controlNetInitialValues, controlNetList } from '@shakkerai/pro-constants/src/controlnet';
import { controlNetOptions, controlOptionsShouldTriggerPreprocess } from '@shakkerai/pro-constants/src/controlnet';
import { findBestSizeByReferenceSize, isStarFlowModel } from '@shakkerai/pro-utils';
import { getSharedData, getTempState, setTempState, useTempState } from '@starflow/app/store';
import { removeControlNet, updateControlNet, refetchControlNetImage } from '../../helpers';
import { appendEmptyControlNet, tryFixControlNetsForBaseType, changeControlNetType } from '../../helpers';
import { updatePromptFromImage, fixHiresOptions } from '../../helpers';
import { updateUnitData } from '@starflow/app/helpers/flow';

import imgCNEmpty from './assets/cn-empty.png';
import imgGuideFace from './assets/guide_face.png';
import imgGuideDepth from './assets/guide_depth.png';
import imgGuideLineart from './assets/guide_lineart.png';
import imgGuidePose from './assets/guide_pose.png';
import imgGuideIpadpter from './assets/guide_ipadapter.png';
import imgGuideEntityControl from './assets/guide_entity_control.png';
import controlnetImgIcon from './assets/drop-zone-img-empty.svg';
import { CommonTipWrapper } from './tips';
import { FaceReference } from './face';
import { DropZone } from './dropzone';
import './reference.scss';

const guideImages = {
  face: imgGuideFace,
  depth: imgGuideDepth,
  canny: imgGuideLineart,
  openpose: imgGuidePose,
  ip_adapter: imgGuideIpadpter,
  entity_control: imgGuideEntityControl,
};

const Options = React.memo(({ disabled, controlNet, baseType, onChange }) => {
  const optionFields = controlNetOptions[controlNet.type]?.(controlNet, baseType) || [];

  const handleChange = useCallback(
    (name, value) => {
      onChange(name, value);
    },
    [onChange],
  );

  const handleChangeNumber = useCallback(
    (name, value) => {
      onChange(name, Number(value.toFixed(2)));
    },
    [onChange],
  );

  return (
    <div className="grid grid-cols-2 gap-y-3 gap-x-8 p-4 pt-0">
      {optionFields.map((item) => {
        if (item.type === 'number') {
          return (
            <div key={item.name} className="flex items-center justify-between h-6">
              {item.label && (
                <span className="w-20 text-ellipsis whitespace-nowrap text-[#7f7f7f] font-grotesk text-xs">
                  {item.label}
                </span>
              )}
              <NumberInputGroup
                size="xs"
                step={item.step}
                min={item.min}
                max={item.max}
                inputWidth={42}
                disabled={disabled || item.disabled}
                value={controlNet[item.name] || item.defaultValue || controlNetInitialValues[item.name]}
                onChange={(value) => handleChangeNumber(item.name, value)}
              />
            </div>
          );
        }

        if (item.type === 'select') {
          return (
            <div key={item.name} className="flex items-center justify-between h-6">
              {item.label && (
                <span className="w-20 text-ellipsis whitespace-nowrap text-[#7f7f7f] font-grotesk text-xs">
                  {item.label}
                </span>
              )}
              <Select
                size="xxs"
                value={(controlNet[item.name] || item.defaultValue) + ''}
                disabled={disabled}
                name={item.name}
                classNames={rightAlignedSelectClassNames}
                withScrollArea={false}
                comboboxProps={{ width: 200, position: 'bottom-end' }}
                className={`cn-select text-xs flex-1 w-20`}
                onChange={(value) => handleChange(item.name, value)}
                data={item.options}
              />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
});

const getControlNetTypeList = (baseType, usedTypes, currentType, isSimple, isInpaint, isStarFlow, hasImg2img) => {
  let filteredNetTypes = controlNetList;

  if (isSimple) {
    filteredNetTypes = filteredNetTypes.filter((item) => simpleModeSupportedControlNets[item.value]);
  }

  if (isInpaint) {
    filteredNetTypes = filteredNetTypes.filter((item) => item.inpaintSupported);
  }

  const supportedItems = [];
  const unsupportedItems = [];

  filteredNetTypes.forEach((item) => {
    const baseTypeNotMatch = !item.baseTypes.includes(baseType) || (item.starFlowOnly && !isStarFlow);
    const unSupportImg2img = item.img2imgUnsupport && hasImg2img;

    if (baseTypeNotMatch) {
      unsupportedItems.push({
        baseTypes: (item.baseTypes ?? []).map((item) => BASETYPE_VALUE_TO_NAME[item]),
        value: item.value,
        label: item.label,
      });
    } else {
      supportedItems.push({
        disabled: usedTypes.includes(item.value) && item.value !== currentType,
        value: item.value,
        label: item.label,
        disabled: unSupportImg2img,
        unSupportImg2img,
      });
    }
  });

  return [supportedItems, unsupportedItems];
};

const selectClassNames = {
  dropdown: 'cn-select-dropdown',
};

const rightAlignedSelectClassNames = {
  ...selectClassNames,
  input: 'text-right pr-6',
};

const DropZoneImgUploadEmpty = React.memo((controlNetImgRef) => {
  const { emptyPlaceholder } = controlNetImgRef;
  if (!emptyPlaceholder) return null;
  return (
    <div className="drop-zone-img-empty">
      <img src={controlnetImgIcon} className="drop-zone-img-empty-icon"></img>
      {emptyPlaceholder}
    </div>
  );
});

const ControlNetOptions = React.memo(
  ({ disabled, generator, controlNetId, usedTypes, baseType, onChange, isInpaint, isSimple }) => {
    const containerRef = useRef(null);
    const [position, setPosition] = useState(null);
    const [showReprocessIcon, setShowReprocessIcon] = useState(false);
    const [advancedParamsVisible, setAdvancedParamsVisible] = useState(false);
    const [unsupportedItemsVisible, setUnsupportedItemsVisible] = useState(false);

    const controlNet = generator.data.controlnets.find((item) => item.id === controlNetId);
    const sourceImage = controlNet?.sourceImage;
    const controlNetType = controlNet?.type;

    const promptMatching = useLoadings('fetch-prompt-' + sourceImage?.id);

    const handleSwitchType = useCallback((type) => onChange({ type }, controlNetId), [controlNetId, onChange]);

    const handleCloseWindow = useCallback(() => {
      setTempState({ exclusiveWindow: null });
    }, []);

    const handleChangeWeight = useCallback(
      (value) => {
        onChange({ controlWeight: Number(value.toFixed(2)) }, controlNetId);
      },
      [controlNetId, onChange],
    );

    const handleChangeOption = useCallback(
      (name, value) => {
        if (controlOptionsShouldTriggerPreprocess.includes(name)) {
          setShowReprocessIcon(true);
        }
        updateControlNet(generator.id, controlNetId, { [name]: value });
      },
      [generator.id, controlNetId],
    );

    const handleGlobalClick = useCallback((event) => {
      if (getSharedData('dragging-element')) return;

      if (containerRef.current?.contains(event.target)) {
        return;
      }

      if (event.target?.closest('[data-role="btn-reload-cn-image"]')) {
        return;
      }

      if (event.target?.closest('[data-trigger-window^="controlnet-options"]')) {
        return;
      }

      if (event.target?.closest('.mantine-Popover-dropdown')) {
        return;
      }

      setTempState({ exclusiveWindow: null });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReprocess = useCallback(() => {
      refetchControlNetImage(generator.id, controlNetId);
    }, [generator.id, controlNetId]);

    const handleMatchPrompt = useCallback(
      async (e) => {
        e.stopPropagation();
        if (promptMatching) return;
        if (!sourceImage) return;
        updatePromptFromImage(sourceImage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
      [sourceImage, promptMatching],
    );

    const handleApplyReferenceSize = useCallback(() => {
      if (!sourceImage) return;

      const fixedSize = findBestSizeByReferenceSize(sourceImage.width, sourceImage.height, MAX_GENERATOR_SIZE);

      if (fixedSize.width !== sourceImage.width || fixedSize.height !== sourceImage.height) {
        notifySuccess(translate('generator_resize_toast'));
      }

      updateUnitData(generator.id, fixedSize);
      fixHiresOptions(generator.id, 'generateSize');
    }, [generator.id, sourceImage]);

    const isStarFlow = !!generator && isStarFlowModel(generator);
    const hasImg2img = !!generator?.data?.imageReference?.url;

    const [supportedCNItems, unsupportedCNItems] = getControlNetTypeList(
      baseType,
      usedTypes,
      controlNetType,
      isSimple,
      isInpaint,
      isStarFlow,
      hasImg2img,
    );

    useEventListener('click', handleGlobalClick);

    useEffect(() => {
      if (controlNetId) {
        const pageWidth = window.innerWidth;
        let position = { x: pageWidth - 827, y: 55 };

        if (!controlNetType) {
          setShowReprocessIcon(false);
        }

        setPosition(position);
      }
    }, [controlNetId, controlNetType]);

    useEffect(() => {
      if (controlNet?.previewImage) {
        setShowReprocessIcon(false);
      }
    }, [controlNet?.previewImage]);

    useEffect(() => {
      mv('tool.pro.reference.show');
    }, []);

    useEffect(() => {
      if (advancedParamsVisible) {
        mc('tool.pro.reference.all_parameters');
      }
    }, [advancedParamsVisible]);

    const [guideVisible, setGuideVisible] = useState(false);

    if (!position || !controlNet) {
      return null;
    }

    const buttonDisabled = disabled || !controlNet.sourceImage;

    // 有些cn参考图上传空状态提醒需要单独处理
    const controlNetImgRef = controlNetImgRefList.find((item) => item.value === controlNetType);
    const emptyPlaceholder = controlNetImgRef && <DropZoneImgUploadEmpty {...controlNetImgRef} />;

    // 只有存在高级设置时才展示高级设置按钮和面板
    const showOptionFields = !!controlNetOptions[controlNet.type];

    return createPortal(
      <Rnd
        scale={1}
        default={position}
        resizeHandleWrapperClass="hidden"
        dragHandleClassName="sub-drag-handle"
        className={`z-[101] image-reference-popup-wrap`}
      >
        <div ref={containerRef} className="z-20 w-[520px] bg-white shadow-light rounded-sm">
          <div className="sub-drag-handle flex items-center justify-between h-9 px-2.5 cursor-move">
            <span className="text-[#2d2d2d] font-bold font-grotesk text-sm">{translate('cn_options_title')}</span>
            <button onClick={handleCloseWindow}>
              <Icon fontSize={16} className="icon-border_close-1 text-[#2d2d2d]" />
            </button>
          </div>
          <div className="max-h-[80vh] overflow-auto tiny-scrollbar">
            <div className="flex flex-col items-stretch px-4 pt-3 border-t border-[#efefef]">
              <span className="mr-4 text-[#2d2d2d] font-grotesk text-xs font-semibold">
                {translate('imgref_dimension')}
              </span>
              <div className={`flex flex-wrap gap-1.5 mt-2 mb-2 empty:mt-0 empty:mb-0`}>
                {supportedCNItems.map((item) => {
                  const selected = item.value === controlNet.type;

                  console.log('item', item);

                  const cnButton = (
                    <button
                      data-value={item.value}
                      disabled={selected || item.disabled}
                      onClick={() => handleSwitchType(item.value)}
                      className={`flex items-center justify-center px-4 ${
                        item.disabled ? 'opacity-60 cursor-not-allowed' : ''
                      } ${
                        selected ? 'bg-primary/10 border-primary text-[#005848]' : 'bg-[#F4F6F8] border-white'
                      } text-xs text-center h-7 rounded-sm ${item.disabled ? '' : 'text-[#2D2D2D]'} border`}
                    >
                      <div
                        className={`w-3 h-3 mr-1.5 bg-white rounded-full ${
                          selected ? 'border-[4px] border-primary' : 'border border-[#D6D7DB]'
                        }`}
                      />
                      <span>{item.label}</span>
                    </button>
                  );

                  if (item.unSupportImg2img && item.disabled) {
                    return (
                      <Tooltip
                        withArrow
                        key={item.value}
                        disabled={!item.disabled}
                        className="translate-y-[5px] scale-90"
                        label={translate('imgref_dimension_conflict_with_img2img')}
                      >
                        {cnButton}
                      </Tooltip>
                    );
                  }

                  if (isSimple && guideImages[item.value]) {
                    return (
                      <Tooltip
                        withArrow
                        key={item.value}
                        color="#2EE8C7"
                        position="bottom"
                        className="p-0"
                        label={
                          <div className="flex flex-col p-5 pt-4 bg-primary rounded border-none shadow-none">
                            <span className="text-sm text-[#2d2d2d] font-medium">{item.label}</span>
                            <img src={guideImages[item.value]} className="w-[240px] h-auto mt-2" />
                          </div>
                        }
                      >
                        {cnButton}
                      </Tooltip>
                    );
                  }

                  return (
                    <Tooltip
                      withArrow
                      key={item.value}
                      disabled={!item.disabled}
                      className="translate-y-[5px] scale-90"
                      label={translate('imgref_dimension_in_use')}
                    >
                      {cnButton}
                    </Tooltip>
                  );
                })}
              </div>
              {unsupportedCNItems.length > 0 && (
                <button
                  onClick={() => setUnsupportedItemsVisible(!unsupportedItemsVisible)}
                  className="flex w-full items-center justify-between mt-2"
                >
                  <span className="text-xs text-[#7f7f7f]">{translate('cn_basetype_unsupported_types')}</span>
                  <Icon
                    fontSize={14}
                    color="#2d2d2d"
                    className={`icon-comfy_xiala ${unsupportedItemsVisible ? 'rotate-180' : ''}`}
                  />
                </button>
              )}

              {unsupportedItemsVisible && unsupportedCNItems.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
                  {unsupportedCNItems.map((item) => (
                    <Tooltip
                      withArrow
                      key={item.value}
                      className="translate-y-[5px] scale-90"
                      label={translate('un_support_dimens_tips', { key: item.baseTypes.join(',') })}
                    >
                      <button
                        key={item.value}
                        className="flex items-center justify-center h-7 px-4 bg-[#f4f6f8] rounded-[2px] cursor-not-allowed"
                      >
                        <div className="w-3 h-3 mr-1.5 border border-[#D6D7DB] rounded-full" />
                        <span className="text-xs text-[#ADADAD]">{item.label}</span>
                      </button>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <div className="mb-2" />
              )}
            </div>
            {controlNet.type === 'face' ? (
              <FaceReference
                isSimple={isSimple}
                generatorId={generator.id}
                generator={generator}
                controlNet={controlNet}
              />
            ) : (
              <>
                <div className="flex items-center px-4">
                  <span className="text-[#2d2d2d] font-grotesk text-xs">{translate('cn_weight')}</span>
                  <div className="w-[150px] flex items-center ml-2">
                    <NumberInputGroup
                      size="xs"
                      step={0.05}
                      min={0}
                      max={1}
                      inputWidth={42}
                      disabled={disabled}
                      value={controlNet.controlWeight}
                      onChange={handleChangeWeight}
                    />
                  </div>
                </div>
                <div className="relative mt-3.5 bg-[#F7F7F7]">
                  <DropZone
                    disabledTip={isInpaint ? translate('cn_source_referece_only_tip') : null}
                    disabled={disabled || isInpaint}
                    generator={generator}
                    allowViewSourceImage={isInpaint}
                    showReprocessIcon={showReprocessIcon}
                    controlNet={controlNet}
                    onRequestReproccess={handleReprocess}
                    emptyPlaceholder={emptyPlaceholder}
                  />
                </div>
                {!isInpaint && (
                  <div className="flex items-center h-[50px]">
                    <button
                      onClick={handleMatchPrompt}
                      disabled={buttonDisabled || promptMatching}
                      className="flex items-center justify-center flex-1 h-[50px] mx-8 hover:opacity-80 disabled:opacity-30 disabled:pointer-events-none"
                    >
                      {promptMatching ? (
                        <Loader size={14} color="#5c636b" />
                      ) : (
                        <Icon className="shk-danchuang-zhuanhuantishici" fontSize={14} color="inherit" />
                      )}
                      <span className="ml-2 text-xs text-[#2d2d2d]">{translate('img_prompt')}</span>
                    </button>
                    <div className="h-4 w-px bg-[#dfdfdf]" />
                    <button
                      onClick={handleApplyReferenceSize}
                      disabled={buttonDisabled || generator.type !== 'generator'}
                      className="flex items-center justify-center flex-1 h-[50px] mx-8 hover:opacity-80 disabled:opacity-30 disabled:pointer-events-none"
                    >
                      <Icon className="shk-tongbuchicun" fontSize={14} color="inherit" />
                      <span className="ml-2 text-xs text-[#2d2d2d]">{translate('img_size')}</span>
                    </button>
                  </div>
                )}
                <div className="w-10">
                  <GuidePopover
                    visible={guideVisible && isSimple}
                    title={translate('imgref_advancetips_title')}
                    position="top"
                    width={260}
                    showFooter={false}
                    offset={{ mainAxis: 0, crossAxis: 100 }}
                    content={translate('imgref_advancetips_content')}
                  />
                </div>
                {!!showOptionFields && (
                  <button
                    disabled={isSimple}
                    onMouseOver={() => setGuideVisible(true)}
                    onMouseLeave={() => setGuideVisible(false)}
                    onClick={() => setAdvancedParamsVisible(!advancedParamsVisible)}
                    className="flex w-full items-center justify-between h-[50px] px-4 disabled:opacity-40 border-t border-[#efefef]"
                  >
                    <span className="text-xs text-[#2d2d2d] font-medium">{translate('cn_all_params')}</span>
                    <Icon
                      className={`icon-comfy_xiala ${advancedParamsVisible ? 'rotate-180' : ''}`}
                      fontSize={14}
                      color="#2d2d2d"
                    />
                  </button>
                )}
                {controlNet.type && showOptionFields && advancedParamsVisible && (
                  <Options
                    disabled={disabled}
                    generatorId={generator.id}
                    baseType={baseType}
                    controlNet={controlNet}
                    onChange={handleChangeOption}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </Rnd>,
      document.body,
    );
  },
);

const ControlNetItem = React.memo(
  ({
    disabled,
    isActive,
    generatorId,
    isInpaint,
    baseType,
    usedTypes,
    value,
    onChange,
    onRemove,
    onToggleOptions,
    isSimple,
    isStarFlow,
    hasImg2img,
  }) => {
    const inputRef = useRef(null);
    const [editable, setEditable] = useState(false);
    const loading = useLoadings(`${generatorId}-controlnet-${value.id}-uploading`);
    const showWarning = !loading && !value.previewImage && value.sourceImage && value.type !== 'reference';

    const [options] = getControlNetTypeList(
      baseType,
      usedTypes,
      value.type,
      isSimple,
      isInpaint,
      isStarFlow,
      hasImg2img,
    );
    const noOptions = !options.length;

    const handleSwitchType = useCallback((type) => onChange({ type }, value.id), [value.id, onChange]);

    const handleChangeWeight = useCallback(
      (weight) => onChange({ controlWeight: Number(weight.toFixed(2)) }, value.id),
      [value.id, onChange],
    );

    const handleRemove = useCallback(() => {
      onRemove(value.id);
    }, [value.id, onRemove]);

    const handleShowOptions = useCallback(() => {
      !noOptions && !disabled && onToggleOptions(value.id);
      setTempState({ showNormalCkptGide: false });
    }, [disabled, value.id, onToggleOptions, noOptions]);

    const handlekeyUp = useCallback((e) => {
      if (e.key === 'Enter') {
        inputRef.current?.blur();
      }
    }, []);

    const isEmpty = !value.type;
    const isFace = value.type === 'face';
    const previewImage = isEmpty || isFace ? value.sourceImage?.url : value.previewImage || value.sourceImage?.url;

    return (
      <div className={`flex items-center h-9 ${isActive ? 'bg-[#f5f5f5]' : ''}`}>
        <Tooltip
          withArrow
          position="bottom"
          disabled={!noOptions}
          className="translate-y-[-5px] scale-90"
          label={translate('cn_basetype_unsupported_tip', { type: BASETYPE_VALUE_TO_LABEL[baseType] })}
        >
          <div className="flex items-center">
            <Tooltip
              withArrow
              disabled={disabled || noOptions}
              position="bottom"
              className="translate-y-[-5px] scale-90"
              label={translate('imgref_settings')}
            >
              <div
                onClick={handleShowOptions}
                data-trigger-window={`controlnet-options-${value.id}`}
                id={`controlnet-options-entry-${value.id}`}
                className={`relative flex items-center justify-center w-8 h-8 cursor-pointer rounded-sm ${
                  disabled || noOptions ? 'opacity-50' : ''
                }`}
              >
                {loading ? (
                  <Loader size={14} color="#999" />
                ) : previewImage ? (
                  <img src={convert2CompressedUrl(previewImage, 64)} className="w-8 h-8 object-contain rounded-sm" />
                ) : showWarning ? (
                  <Icon className="icon-border_removed" fontSize={14} color="#999" />
                ) : (
                  <img src={imgCNEmpty} className="w-8 h-8 object-contain rounded-sm" />
                )}
                <div className="absolute inset-0 border border-black/10 rounded-sm" />
              </div>
            </Tooltip>
            <Select
              size="xxs"
              disabled={disabled || noOptions}
              value={value.type}
              onChange={handleSwitchType}
              classNames={selectClassNames}
              withScrollArea={true}
              maxDropdownHeight={220}
              placeholder={translate('imgref_noselece')}
              className={`cn-select bordered flex-1 mx-2`}
              comboboxProps={{ width: 200, position: 'bottom-start' }}
              data={options}
            />
            {isEmpty || isFace ? (
              <div className="w-8" />
            ) : (
              <Tooltip
                withArrow
                position="bottom"
                className="translate-y-[-5px] scale-90"
                label={translate('imgref_similarity')}
              >
                <NumberInput
                  size="xxs"
                  step={0.1}
                  min={0}
                  max={1}
                  ref={inputRef}
                  disabled={disabled}
                  readOnly={!editable}
                  value={value.controlWeight}
                  onKeyUp={handlekeyUp}
                  onClick={() => setEditable(true)}
                  onBlur={() => setEditable(false)}
                  onChange={handleChangeWeight}
                  className="cn-weight-input"
                />
              </Tooltip>
            )}
          </div>
        </Tooltip>
        <div className="flex-1" />
        <button
          onClick={handleRemove}
          className="flex items-center ml-2 justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
        >
          <Icon className="icon-proshouqi" fontSize={12} color="#666" />
        </button>
      </div>
    );
  },
);

export const Reference = React.memo(({ disabled, generator = {} }) => {
  const { controlnets } = generator.data;
  const mode = getTempState('globalMode');
  const isSimple = mode === 'simple';
  const isInpaint = generator?.type === 'inpaint';
  const baseType = generator.data.baseType;
  // 是否是星流模型, 只有星流模型支持entity_control
  const isStarFlow = !!generator && isStarFlowModel(generator);
  // 是否已有图生图, 已有图生图不支持entity_control
  const hasImg2img = !!generator?.data?.imageReference?.url;
  const showRemixReferenceReplaceTip = useTempState('showRemixReferenceReplaceTip') && generator?.type === 'primary';
  const openedSubWindow = useTempState('exclusiveWindow');

  const handleCloseRemixReferenceReplaceTip = useCallback(() => {
    setTempState({ showRemixReferenceReplaceTip: false });
  }, []);

  const handleChange = useCallback(
    (value, id) => {
      if (value?.type) {
        changeControlNetType(generator.id, id, value.type);
      } else {
        updateControlNet(generator.id, id, value);
      }
    },
    [generator.id],
  );

  const handleRemove = useCallback(
    (id) => {
      removeControlNet(generator.id, id);
    },
    [generator.id],
  );

  const handleToggleOptions = useCallback(
    (controlNetId) => {
      setTempState({
        exclusiveWindow: `controlnet-options-${controlNetId}`,
      });
    },
    [openedSubWindow],
  );

  const handleAddItem = useCallback(async () => {
    const cId = await appendEmptyControlNet(generator.id);
    if (cId) {
      handleToggleOptions(cId);
      setTempState({ showNormalCkptGide: false });
    }
    if (!isSimple) {
      mc('tool.pro.reference.add');
    }
  }, [generator.id, handleToggleOptions, isSimple]);

  // 支持的类型增加星流专属的类型
  const supportedTypes = useMemo(
    () =>
      controlNetList.filter((item) => {
        const { baseTypes, starFlowOnly } = item;
        if (starFlowOnly && !isStarFlow) {
          return false;
        }
        return baseTypes.includes(baseType);
      }),
    [baseType, isStarFlow],
  );
  const usedTypes = useMemo(() => controlnets.map((item) => item.type), [controlnets]);

  const openedControlNetId = useMemo(() => {
    return openedSubWindow?.includes('controlnet-options') ? openedSubWindow.split('controlnet-options-')[1] : null;
  }, [openedSubWindow]);

  // const controlNetId = useMemo(() => {
  //   return (generator?.params?.controlnets || [])?.[0]?.id;
  // }, [generator]);
  // const handleOpenProMode = useCallback(() => {
  //   updateOpenedSubWindow(`controlnet-options-${controlNetId}`);
  // }, [controlNetId, updateOpenedSubWindow]);

  useEffect(() => {
    tryFixControlNetsForBaseType(generator.id);
  }, [generator.id, generator.data.baseType, generator.data.presetBaseModelId]);

  const noSupportedTypes = supportedTypes.length === 0;
  const disabledForFlux = baseType === MODEL_FLUX && controlnets.length >= 1;
  const addMoreDisabledTip = noSupportedTypes
    ? 'cn_basetype_unsupported_tip'
    : disabledForFlux
      ? 'cn_flux_count_limit_tip'
      : null;

  return (
    <div className="relative" id="imgReferrerEntry">
      <div className="flex items-center justify-between h-4 ">
        <h5 className="flex-1 text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
          <span>{translate('imgref_title')}</span>
          <span className="text-[#b2b2b2]">&ensp;Control-Net</span>
        </h5>
        <Tooltip
          disabled={!addMoreDisabledTip}
          label={translate(addMoreDisabledTip, { type: BASETYPE_VALUE_TO_LABEL[baseType] })}
        >
          <button
            onClick={handleAddItem}
            disabled={controlnets?.length >= 4 || noSupportedTypes || disabled || disabledForFlux}
            data-trigger-window="controlnet-options"
            className="flex items-center justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
          >
            <Icon className="icon-prozhankai" fontSize={12} color="#666" />
          </button>
        </Tooltip>
      </div>
      {showRemixReferenceReplaceTip && (
        <CommonTipWrapper
          position="left-start"
          title={translate('share_remix_ref_replace')}
          subTitle={translate('share_remix_ref_replace_tip')}
          buttonText={translate('share_remix_replace_ok_button')}
          arrowOffset={20}
          onButtonClick={handleCloseRemixReferenceReplaceTip}
        >
          <div className="translate-y-6 pointer-events-none" />
        </CommonTipWrapper>
      )}
      <div
        onClick={handleCloseRemixReferenceReplaceTip}
        className="mt-3 pb-3 last:pb-0 empty:mt-0 empty:pb-0 space-y-2.5"
      >
        {controlnets?.map((item) => (
          <ControlNetItem
            key={item.id}
            value={item}
            baseType={baseType}
            usedTypes={usedTypes}
            disabled={disabled}
            onChange={handleChange}
            onRemove={handleRemove}
            isInpaint={isInpaint}
            generatorId={generator.id}
            onToggleOptions={handleToggleOptions}
            isActive={openedControlNetId === item.id}
            isSimple={isSimple}
            isStarFlow={isStarFlow}
            hasImg2img={hasImg2img}
          />
        ))}
      </div>
      {openedControlNetId && (
        <ControlNetOptions
          disabled={disabled}
          usedTypes={usedTypes}
          baseType={baseType}
          key={openedControlNetId}
          controlNetId={openedControlNetId}
          generator={generator}
          onChange={handleChange}
          isInpaint={isInpaint}
          isSimple={isSimple}
        />
      )}
    </div>
  );
});
